import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { translations } from './translations';

const STORAGE_KEY = 'salient_lang';
const DEFAULT_LANG = 'es';
const SUPPORTED = ['es', 'en'];

const LanguageContext = createContext({
    lang: DEFAULT_LANG,
    setLang: () => {},
    toggleLang: () => {},
    t: (key) => key,
});

// Resuelve una ruta con puntos ("home.hero.subtitle") dentro de un objeto.
const resolve = (obj, path) =>
    path.split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);

export const LanguageProvider = ({ children }) => {
    const [lang, setLangState] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved && SUPPORTED.includes(saved)) return saved;
        } catch (e) { /* noop */ }
        return DEFAULT_LANG;
    });

    useEffect(() => {
        try { localStorage.setItem(STORAGE_KEY, lang); } catch (e) { /* noop */ }
        if (typeof document !== 'undefined') document.documentElement.lang = lang;
    }, [lang]);

    const setLang = useCallback((value) => {
        if (SUPPORTED.includes(value)) setLangState(value);
    }, []);

    const toggleLang = useCallback(() => {
        setLangState((prev) => (prev === 'es' ? 'en' : 'es'));
    }, []);

    // t(key): devuelve el valor (string, array u objeto) del idioma actual.
    // Si falta, cae al español y, en último caso, a la propia clave.
    const t = useCallback((key) => {
        const current = resolve(translations[lang], key);
        if (current !== undefined) return current;
        const fallback = resolve(translations[DEFAULT_LANG], key);
        return fallback !== undefined ? fallback : key;
    }, [lang]);

    return (
        <LanguageContext.Provider value={{ lang, setLang, toggleLang, t }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => useContext(LanguageContext);

export default LanguageContext;
